import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Sponsor, SponsorLayout } from "src/types";

import { Stars } from "@/components/icons/stars";
import ImportBase from "@/components/import-list/import-base-component";

export type SponsorCardProps = {
  sponsor: Sponsor;
};

export type ThemedSponsorCard = React.FunctionComponent<SponsorCardProps>;

export default function SponsorsSettings() {
  const {
    actions: { addNodeTree },
    query,
    store,
  } = useEditor();

  const { t } = useTranslation();
  const { nodeId, layout } = useNode((node) => {
    const nodeId = node
      .getLinkedNodes()
      .find((node) => node.id === "sponsors-list")?.node.id;

    return {
      nodeId: nodeId || node.id,
      layout: node.props?.layout,
    } as {
      nodeId: string;
      layout: SponsorLayout | undefined;
    };
  });

  const handleImportSponsors = React.useCallback(
    (sponsors?: Sponsor[]) => {
      if (!nodeId) return;
      if (!sponsors?.length) return;
      // Add a SponsorCard for each sponsor imported
      const component = store.resolver["SponsorCard"];
      sponsors.forEach((sponsor) => {
        const nodeTree = query.parseReactElementAsNodeTree(
          React.createElement(component, { sponsor })
        );
        addNodeTree(nodeTree, nodeId);
      });
    },
    [store, query, addNodeTree, nodeId]
  );

  const message =
    layout === "golden"
      ? "preference-panel.sponsors.add-golden-sponsors-details"
      : layout === "silver"
      ? "preference-panel.sponsors.add-silver-sponsors-details"
      : layout === "bronze"
      ? "preference-panel.sponsors.add-bronze-sponsors-details"
      : "preference-panel.sponsors.add-sponsors-details";

  return (
    <ImportBase
      title={t("preference-panel.sponsors.sponsors")}
      type="sponsors"
      icon={Stars}
      importText={t("preference-panel.sponsors.import-sponsors-from-event")}
      importButtonText={t("preference-panel.sponsors.import-sponsors")}
      onImportClick={handleImportSponsors}
      mainEmptyText={t("preference-panel.sponsors.no-sponsors-available")}
      subEmptyText={t(message)}
    />
  );
}
